import { useState } from 'react'
import { Link } from 'react-router-dom'
import { styled } from '@linaria/react'
import { from, minimalVars as v } from '@ovasabi/ui-minimal/tokens'
import { usePublicPromos } from '../../lib/discover'

const BannerContainer = styled.div`
  margin-block: ${v.space.lg};
  padding: ${v.space.md} ${v.space.lg};
  border-radius: ${v.radius.xl};
  background: linear-gradient(135deg, #FFF9F2 0%, #FFF1E6 100%);
  border: 1px solid #F5DECB;
  display: flex;
  flex-direction: column;
  gap: ${v.space.md};
  box-shadow: 0 4px 16px rgba(197, 41, 28, 0.04);
  animation: promo-enter 220ms ease-out both;

  ${from('page')} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: ${v.space.lg} ${v.space.xl};
  }

  @keyframes promo-enter {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${v.space.xs};
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${v.space.xs};
  background: #E31837;
  color: ${v.color.textInverse};
  font-size: ${v.typography.metaSize};
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: ${v.radius.pill};
  width: fit-content;
`

const Title = styled.h2`
  /* The banner title stays fluid: h2Size is fixed at 22px and oversizes it on a phone. */
  font-size: clamp(1.2rem, 2.5vw, 1.6rem);
  font-weight: 900;
  color: #1A1A1A;
  margin: 0;
  line-height: 1.2;
`

const Description = styled.p`
  font-size: ${v.typography.bodySize};
  color: #555555;
  margin: 0;
  max-width: 50ch;
  line-height: ${v.typography.lineHeightBody};
`

const Right = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${v.space.sm};
  flex-shrink: 0;
`

const CodeChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${v.space.xs};
  background: ${v.color.bgSurface};
  border: 1.5px dashed #E31837;
  color: #E31837;
  padding: 8px 16px;
  border-radius: ${v.radius.pill};
  font-family: monospace;
  font-size: ${v.typography.bodySize};
  font-weight: 800;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;

  &:hover {
    background: #FFF1E6;
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }
`

const Cta = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #E31837;
  color: ${v.color.textInverse};
  font-weight: 800;
  font-size: ${v.typography.bodySize};
  padding: 10px 20px;
  border-radius: ${v.radius.pill};
  text-decoration: none;
  transition: opacity 0.15s ease, transform 0.1s ease;

  &:hover {
    opacity: 0.92;
    transform: scale(1.02);
  }
`

const LoadingLine = styled.span`
  display: block;
  width: 100%;
  max-width: 34ch;
  height: 14px;
  border-radius: ${v.radius.pill};
  background: linear-gradient(90deg, #f5decb 0%, #fff8ed 48%, #f5decb 100%);
  background-size: 220% 100%;
  animation: promo-shimmer 1.1s ease-in-out infinite;

  &:first-child {
    max-width: 12ch;
    height: 22px;
  }

  @keyframes promo-shimmer {
    from {
      background-position: 120% 0;
    }
    to {
      background-position: -120% 0;
    }
  }
`

export const PromoDealsBanner = () => {
  const [copied, setCopied] = useState(false)
  const { promos, loading } = usePublicPromos()

  if (loading) {
    return (
      <BannerContainer role="status" aria-busy="true" aria-label="Loading special promotion">
        <Left>
          <LoadingLine />
          <LoadingLine />
          <LoadingLine style={{ maxWidth: '24ch' }} />
        </Left>
        <Right>
          <LoadingLine style={{ maxWidth: '11ch', height: 38 }} />
          <LoadingLine style={{ maxWidth: '12ch', height: 38 }} />
        </Right>
      </BannerContainer>
    )
  }

  if (promos.length === 0) {
    return null
  }

  const promo = promos[0]

  const handleCopy = () => {
    void navigator.clipboard.writeText(promo.code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <BannerContainer role="region" aria-label="Special promotion offer">
      <Left>
        <Badge>Limited Time Offer</Badge>
        <Title>{promo.discount_percent}% off — {promo.name}</Title>
        <Description>
          Taste authentic Nigerian cooking prepared with love by verified local chefs. Use code at checkout:
        </Description>
      </Left>
      <Right>
        <CodeChip type="button" onClick={handleCopy} title="Click to copy coupon code">
          <span>{copied ? '✓ COPIED!' : `CODE: ${promo.code}`}</span>
        </CodeChip>
        <Cta to="/kitchens">Explore Kitchens →</Cta>
      </Right>
    </BannerContainer>
  )
}
